import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageIndices } from '../common/i-doc/i-doc.registry.ts';
import { PageHint } from '../components/common/PageHint';
import { DashboardLayout } from '../components/DashboardLayout';
import { AdaptiveTabs, AdaptiveTabItem, useAdaptiveTab } from '../components/common/AdaptiveTabs';
import { getQuestionnaires } from '../services/questionnaires/questionnaire.service';
import { Questionnaire } from '../types/questionnaire';
import {
  Box,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  CardActionArea,
} from '@mui/material';

const QUESTIONNAIRES_TABS: AdaptiveTabItem[] = [
  { value: 'pending', label: 'Не пройдені' },
  { value: 'completed', label: 'Пройдені' },
];

const isCompleted = (q: Questionnaire) => q.status === 'completed';

const QuestionnairesPageContent: React.FC = () => {
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const currentTab = useAdaptiveTab(QUESTIONNAIRES_TABS, { searchParamKey: 'tab', defaultValue: 'pending', storageKey: 'questionnaires-tab' });

  useEffect(() => {
    const fetchQuestionnaires = async () => {
      setLoading(true);
      const data = await getQuestionnaires();
      setQuestionnaires(data);
      setLoading(false);
    };

    fetchQuestionnaires();
  }, []);

  const handleCardClick = (id: number) => {
    navigate(`/questionnaires/${id}`);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('uk-UA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  const visibleQuestionnaires = questionnaires.filter(q =>
    currentTab === 'completed' ? isCompleted(q) : !isCompleted(q),
  );

  return (
    <Box data-i-doc={PageIndices.QUESTIONNAIRES}>
      <PageHint iDoc={PageIndices.QUESTIONNAIRES} />
      <AdaptiveTabs
        tabs={QUESTIONNAIRES_TABS}
        searchParamKey="tab"
        defaultValue="pending"
        storageKey="questionnaires-tab"
        mobileThreshold={1}
      />
      <Box sx={{ pt: 3 }}>
      {visibleQuestionnaires.length === 0 ? (
        <Typography>
          {currentTab === 'completed' ? 'Немає пройдених опитувальників.' : 'Немає непройдених опитувальників.'}
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {visibleQuestionnaires.map((q) => (
            <Grid item xs={12} sm={6} md={4} key={q.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardActionArea
                  onClick={() => handleCardClick(q.id)}
                  sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="div">
                      {q.name}
                    </Typography>
                    {q.invitedByExpert && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Запросив: {q.invitedByExpert.name ?? `Експерт #${q.invitedByExpert.id}`}
                      </Typography>
                    )}
                    <Typography sx={{ mt: 1.5 }} color="text.secondary">
                      {q.description}
                    </Typography>
                  </CardContent>
                  <Box sx={{ p: 2, pt: 0 }}>
                    {q.completionRate !== null && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Прогрес: {q.completionRate}%
                        </Typography>
                        <LinearProgress variant="determinate" value={q.completionRate} />
                      </Box>
                    )}
                    {q.lastSavedAt && (
                      <Typography variant="caption" color="text.secondary">
                        Останнє збереження: {formatDate(q.lastSavedAt)}
                      </Typography>
                    )}
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      </Box>
    </Box>
  );
};

const QuestionnairesPage: React.FC = () => {
  return (
    <DashboardLayout>
      <QuestionnairesPageContent />
    </DashboardLayout>
  );
};

export default QuestionnairesPage;
